import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Job } from '../types';
import StatusBadge from './ui/StatusBadge';
import EmptyState from './ui/EmptyState';
import { formatDuration } from '../utils/time';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileUp,
  Loader2,
  RefreshCw,
} from 'lucide-react';

type ActivityItem = { type: 'job'; data: Job };

function isJobDone(job: Job): boolean {
  return ['completed', 'failed', 'aborted', 'needs_review'].includes(job.status);
}

function itemTimestamp(item: ActivityItem): string {
  return item.data.created_at;
}

export default function RecentActivity({ limit = 20 }: { limit?: number }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const jobsData = await apiClient.get<{ items: Job[] }>(`/jobs/recent?limit=${limit}`);
      const jobs: ActivityItem[] = (jobsData.items || []).map((j) => ({ type: 'job', data: j }));
      const merged = jobs
        .sort((a, b) => new Date(itemTimestamp(b)).getTime() - new Date(itemTimestamp(a)).getTime())
        .slice(0, limit);
      setItems(merged);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load recent activity';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [limit]);

  const anyRunning = items.some((item) => !isJobDone(item.data));

  useEffect(() => {
    if (!anyRunning) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [anyRunning, limit]);

  const Icon = (_item: ActivityItem) => {
    return FileUp;
  };

  const Status = (item: ActivityItem) => {
    return item.data.status;
  };

  const title = (item: ActivityItem) => {
    const meta = (item.data.metadata_ as Record<string, unknown> | undefined) ?? (item.data.metadata as Record<string, unknown> | undefined);
    return `Protocol decompile: ${meta?.protocol_filename || `Job ${item.data.id.slice(0, 8)}`}`;
  };

  const duration = (item: ActivityItem) => {
    const meta = item.data.metadata as Record<string, unknown> | null | undefined;
    const debug = meta?.extraction_debug as Record<string, unknown> | undefined;
    if (item.type === 'job' && item.data.elapsed_seconds != null) {
      return formatDuration(item.data.elapsed_seconds);
    }
    if (debug && typeof debug.duration_seconds === 'number') {
      return formatDuration(debug.duration_seconds);
    }
    return '';
  };

  const link = (item: ActivityItem) => {
    if (item.data.study_id) return `/studies/${item.data.study_id}`;
    return undefined;
  };

  return (
    <div className="panel">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
        </div>
        <button
          onClick={load}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          title="Refresh"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {!error && items.length === 0 && !loading && (
        <EmptyState
          icon={Activity}
          title="No recent activity"
          description="Upload a protocol or ODM-XML file to see activity here."
        />
      )}

      <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[28rem] overflow-y-auto">
        {items.map((item) => {
          const ItemIcon = Icon(item);
          const itemLink = link(item);
          return (
            <div
              key={`${item.type}-${item.data.id}`}
              onClick={() => itemLink && navigate(itemLink)}
              className={`px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                itemLink ? 'cursor-pointer' : ''
              }`}
            >
              <div className="mt-0.5 shrink-0">
                <ItemIcon className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {title(item)}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={Status(item)} size="sm" />
                  {duration(item) && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {duration(item)}
                    </span>
                  )}
                </div>
                {'error_message' in item.data && item.data.error_message && (
                  <p className="text-xs text-red-600 dark:text-red-300 mt-1 line-clamp-2">
                    {item.data.error_message}
                  </p>
                )}
              </div>
              {['completed', 'needs_review'].includes(item.data.status) ? (
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-1" />
              ) : !isJobDone(item.data) ? (
                <Loader2 className="h-4 w-4 text-indigo-500 animate-spin shrink-0 mt-1" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
